/**
 * Price Display Component with Local Currency Support
 * Shows prices in both USD and local currency (when applicable)
 */

import React from 'react';
import { LocalizedLineItem, LocalizedPricingBreakdown } from '@/services/pricing/unifiedPricingEngine';
import { LocalizedPrice } from '@/services/pricing/currencyService';

interface PriceDisplayProps {
  amount: number;
  localized?: LocalizedLineItem | LocalizedPrice; // Support both old and new format
  showUSD?: boolean; // Show USD even when displaying local currency
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export default function PriceDisplay({ 
  amount, 
  localized, 
  showUSD = true,
  size = 'md',
  className = ''
}: PriceDisplayProps) {
  // Size classes
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };
  
  // If no localized price or it's USD, show simple format. A value without `formatted` is a whole
  // breakdown passed by mistake (it needs a line item such as localized.total): show dollars, not blank.
  if (!localized || localized.currency === 'USD' || !('formatted' in localized) || !localized.formatted) {
    return (
      <span className={`${sizeClasses[size]} ${className} font-semibold`}>
        ${amount.toFixed(2)}
      </span>
    );
  }
  
  // Show localized price with USD reference
  return (
    <span className={`${className}`}>
      <span className={`${sizeClasses[size]} font-semibold`}>
        {localized.formatted}
        {'isApproximate' in localized && localized.isApproximate && (
          <span className="text-xs align-super ml-1">*</span>
        )}
      </span>
      {showUSD && (
        <span className="text-xs font-medium ml-2 opacity-75">
          (${amount.toFixed(2)} USD)
        </span>
      )}
    </span>
  );
}

interface PriceBreakdownDisplayProps {
  breakdown: {
    basePDF: number;
    additionalPages: number;
    ereader: number;
    print: number;
    shipping: number;
    subtotal: number;
    total: number;
    localized?: LocalizedPricingBreakdown;
  };
  pageCount?: number;
  isMultiCharacter?: boolean;
}

export function PriceBreakdownDisplay({ 
  breakdown, 
  pageCount = 8,
  isMultiCharacter = false 
}: PriceBreakdownDisplayProps) {
  const additionalPageCount = Math.max(0, pageCount - 8);
  
  return (
    <div className="space-y-2 text-sm">
      {/* Base PDF */}
      <div className="flex justify-between">
        <span className="text-gray-700">PDF Book (includes first 8 pages)</span>
        <PriceDisplay 
          amount={breakdown.basePDF} 
          localized={breakdown.localized?.basePDF}
          showUSD={false}
          size="sm"
        />
      </div>
      
      {/* Additional pages */}
      {breakdown.additionalPages > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-700">
            {additionalPageCount} additional pages @ ${isMultiCharacter ? '1.30' : '1.00'}
          </span>
          <PriceDisplay 
            amount={breakdown.additionalPages} 
            localized={breakdown.localized?.additionalPages}
            showUSD={false}
            size="sm"
          />
        </div>
      )}
      
      {/* E-reader */}
      {breakdown.ereader > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-700">E-Reader Edition (EPUB)</span>
          <PriceDisplay 
            amount={breakdown.ereader} 
            localized={breakdown.localized?.ereader}
            showUSD={false}
            size="sm"
          />
        </div>
      )}
      
      {/* Print */}
      {breakdown.print > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-700">Printed Book</span>
          <PriceDisplay 
            amount={breakdown.print} 
            localized={breakdown.localized?.print}
            showUSD={false}
            size="sm"
          />
        </div>
      )}
      
      {/* Subtotal */}
      {breakdown.shipping > 0 && (
        <>
          <div className="border-t pt-2 flex justify-between">
            <span className="text-gray-700">Subtotal</span>
            <PriceDisplay 
              amount={breakdown.subtotal} 
              localized={breakdown.localized?.subtotal}
              showUSD={false}
              size="sm"
            />
          </div>
          
          {/* Shipping */}
          <div className="flex justify-between">
            <span className="text-gray-700">Shipping</span>
            <PriceDisplay 
              amount={breakdown.shipping} 
              localized={breakdown.localized?.shipping}
              showUSD={false}
              size="sm"
            />
          </div>
        </>
      )}
      
      {/* Total */}
      <div className="border-t pt-2 flex justify-between font-semibold">
        <span>Total</span>
        <PriceDisplay 
          amount={breakdown.total} 
          localized={breakdown.localized?.total}
          showUSD={true}
          size="md"
          className="text-right"
        />
      </div>
      
      {/* Exchange rate disclaimer */}
      {breakdown.localized && breakdown.localized.isApproximate && (
        <div className="text-xs text-gray-700 italic pt-2">
          * {breakdown.localized.disclaimer}
        </div>
      )}
    </div>
  );
}


// Hook for using localized pricing
export function useLocalizedPricing(amount: number, countryCode?: string) {
  const [localized, setLocalized] = React.useState<LocalizedPrice | null>(null);
  const [loading, setLoading] = React.useState(false);
  
  React.useEffect(() => {
    if (!countryCode || countryCode === 'US') {
      setLocalized(null);
      return;
    }
    
    const fetchLocalized = async () => {
      setLoading(true);
      try {
        const { default: currencyService } = await import('@/services/pricing/currencyService');
        const result = await currencyService.convertToLocalCurrency(amount, countryCode);
        setLocalized(result);
      } catch (error) {
        console.error('Failed to get localized pricing:', error);
        setLocalized(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocalized();
  }, [amount, countryCode]);
  
  return { localized, loading };
}
